/**
 * Production Schedule View Mode Configuration
 * Defines all available view modes and their behavior
 */

import React from 'react';
import {
  Dashboard,
  Work,
  Category,
  Business,
  Person,
  LocationOn,
  Widgets,
} from '@mui/icons-material';
import type {
  ViewModeConfig,
  ProductionScheduleConfig,
} from 'types/productionSchedule.types';
import { utils } from 'shared/intrasyncUtils';

// Helper function for dynamic row height (mold-based)
const getMoldBasedRowHeight = (date: string, entities: unknown[]): number => {
  const moldCounts: { [moldNumber: string]: number } = {};

  entities.forEach((entity: any) => {
    const moldNumber = entity.moldNumber as string;
    if (moldNumber) {
      moldCounts[moldNumber] = (moldCounts[moldNumber] || 0) + 1;
    }
  });

  let maxCount = 0;
  for (const moldNumber in moldCounts) {
    if (moldCounts[moldNumber] > maxCount) {
      maxCount = moldCounts[moldNumber];
    }
  }

  if (maxCount >= 2 && maxCount <= 4) {
    return maxCount * 52;
  }
  if (maxCount > 4) {
    return maxCount * 51;
  }
  return 54;
};

// View Mode Configurations
export const VIEW_MODE_CONFIGS: ViewModeConfig[] = [
  // Overall View - All jobs
  {
    id: 'overall',
    label: 'All Jobs',
    icon: <Dashboard fontSize="small" />,
    description: 'View all jobs grouped by job number',

    groupBy: 'jobNumber',
    headerKey: 'jobNumber',
    columnWidth: 200,
    columnTitle: (value) => `Job ${value}`,

    autoDateRange: false,
    defaultDateOffset: { start: -14, end: 56 },

    rowHeightMode: 'fixed',
    fixedRowHeight: 100,

    metrics: [
      {
        label: 'Total Panels',
        field: 'panelCount',
        aggregation: 'count',
        format: 'number',
        unit: 'panels',
      },
    ],
  },

  // Per-Job View - Single job by mold
  {
    id: 'job',
    label: 'Per Job',
    icon: <Work fontSize="small" />,
    description: 'View single job grouped by mold number',

    groupBy: 'moldNumber',
    headerKey: 'moldNumber',
    columnWidth: 230,

    requiredQueryParams: ['jobNumber'],

    autoDateRange: true, // Calculate from job's casting dates

    rowHeightMode: 'custom',
    dynamicRowHeightFn: getMoldBasedRowHeight,

    contextSelector: {
      type: 'job',
      label: 'Select Job',
      field: 'jobNumber',
      required: true,
    },

    additionalFilters: [
      {
        field: 'showMu',
        label: 'Show MU/FBF Molds',
        type: 'toggle',
      },
    ],

    metrics: [
      {
        label: 'Panels Cast',
        field: 'panelCount',
        aggregation: 'count',
        format: 'number',
        target: 50,
        unit: 'panels',
      },
      {
        label: 'Total Area',
        field: 'area',
        aggregation: 'sum',
        format: 'area',
        unit: 'sq ft',
      },
    ],
  },

  // Department View - By department
  {
    id: 'department',
    label: 'By Department',
    icon: <Category fontSize="small" />,
    description: 'View all jobs grouped by department',

    groupBy: 'department',
    headerKey: 'department',
    columnWidth: 220,

    autoDateRange: false,
    defaultDateOffset: { start: -14, end: 56 },

    rowHeightMode: 'fixed',
    fixedRowHeight: 100,

    contextSelector: {
      type: 'department',
      label: 'Filter Department',
      field: 'department',
      required: false,
    },

    metrics: [
      {
        label: 'Panels Scheduled',
        field: 'panelCount',
        aggregation: 'count',
        format: 'number',
        target: 75,
        unit: 'panels',
      },
      {
        label: 'Total Weight',
        field: 'weight',
        aggregation: 'sum',
        format: 'weight',
        unit: 'lbs',
      },
      {
        label: 'Avg Panel Size',
        field: 'area',
        aggregation: 'avg',
        format: 'area',
        unit: 'sq ft',
      },
    ],
  },

  // Plant View - By plant location
  {
    id: 'plant',
    label: 'By Plant',
    icon: <Business fontSize="small" />,
    description: 'View production grouped by plant location',

    groupBy: 'plant',
    headerKey: 'plant',
    columnWidth: 200,

    autoDateRange: false,
    defaultDateOffset: { start: -14, end: 56 },

    rowHeightMode: 'fixed',
    fixedRowHeight: 100,

    contextSelector: {
      type: 'plant',
      label: 'Filter Plant',
      field: 'plant',
      required: false,
    },

    metrics: [
      {
        label: 'Panels Scheduled',
        field: 'panelCount',
        aggregation: 'count',
        format: 'number',
        target: 100,
        unit: 'panels',
      },
      {
        label: 'Plant Capacity',
        field: 'capacity',
        aggregation: 'sum',
        format: 'percentage',
        target: 80,
        unit: '%',
      },
    ],
  },

  // Customer View - By customer
  {
    id: 'customer',
    label: 'By Customer',
    icon: <Person fontSize="small" />,
    description: 'View production grouped by customer',

    groupBy: 'customerName',
    headerKey: 'customerName',
    columnWidth: 220,

    autoDateRange: false,
    defaultDateOffset: { start: -14, end: 56 },

    rowHeightMode: 'fixed',
    fixedRowHeight: 100,

    contextSelector: {
      type: 'customer',
      label: 'Filter Customer',
      field: 'customerName',
      required: false,
    },

    metrics: [
      {
        label: 'Panels Scheduled',
        field: 'panelCount',
        aggregation: 'count',
        format: 'number',
        unit: 'panels',
      },
    ],
  },

  // Production Area View
  {
    id: 'productionArea',
    label: 'By Production Area',
    icon: <LocationOn fontSize="small" />,
    description: 'View production grouped by production area',

    groupBy: 'productionArea',
    headerKey: 'productionArea',
    columnWidth: 200,
    columnTitle: (value) => value?.toString() || 'Unknown',

    autoDateRange: false,
    defaultDateOffset: { start: -14, end: 56 },

    rowHeightMode: 'fixed',
    fixedRowHeight: 100,

    contextSelector: {
      type: 'custom',
      label: 'Filter Area',
      field: 'productionArea',
      required: false,
    },

    metrics: [
      {
        label: 'Panels Scheduled',
        field: 'panelCount',
        aggregation: 'count',
        format: 'number',
        target: 60,
        unit: 'panels',
      },
      {
        label: 'Area Utilization',
        field: 'utilization',
        aggregation: 'avg',
        format: 'percentage',
        target: 85,
        unit: '%',
      },
    ],
  },

  // Product Type View
  {
    id: 'productType',
    label: 'By Product Type',
    icon: <Widgets fontSize="small" />,
    description: 'View production grouped by product type',

    groupBy: 'productType',
    headerKey: 'productType',
    columnWidth: 200,

    autoDateRange: false,
    defaultDateOffset: { start: -14, end: 56 },

    rowHeightMode: 'fixed',
    fixedRowHeight: 100,

    contextSelector: {
      type: 'custom',
      label: 'Filter Product Type',
      field: 'productType',
      required: false,
    },

    metrics: [
      {
        label: 'Panels Scheduled',
        field: 'panelCount',
        aggregation: 'count',
        format: 'number',
        unit: 'panels',
      },
      {
        label: 'Total Volume',
        field: 'volume',
        aggregation: 'sum',
        format: 'number',
        unit: 'cu yd',
      },
    ],
  },
];

// Main Production Schedule Configuration
export const PRODUCTION_SCHEDULE_CONFIG: ProductionScheduleConfig = {
  viewModes: VIEW_MODE_CONFIGS,
  defaultViewMode: 'overall',
  enableViewModeSwitching: true,
  showMetrics: true,
  enableDateRangeSelector: true,
  enableColumnVisibility: true,
};

// Helper to get view mode config by ID
export const getViewModeConfig = (
  id: string
): ViewModeConfig | undefined => {
  return VIEW_MODE_CONFIGS.find((config) => config.id === id);
};
